import { type ReactNode, useEffect } from "react";

import { Controller, type UseFormReturn } from "react-hook-form";

import {
  Button,
  Icon,
  Link,
  Select,
  Text,
  Tooltip,
  useModal,
} from "@alpacahq/ui/old";

import type { OwnerDetails, UserConfigurations } from "~/v2/api/rest/account";
import Switch from "~/v2/components/common/switch";

const MAX_MARGIN_MULTIPLIER_OPTIONS = [4, 2, 1];
const PTP_DISCLOSURE_LINK =
  "https://files.alpaca.markets/disclosures/library/PTP+Withholding+Disclosure.pdf";

export const fields = [
  "suspend_trade",
  "trade_confirm_email",
  "fractional_trading",
  "max_margin_multiplier",
  "no_shorting",
  "ptp_no_exception_entry",
  "max_options_trading_level",
] as const;

export type FieldValue = string | number | boolean | null | undefined;

export type Fields = {
  [key in (typeof fields)[number]]: FieldValue;
};

type FormInputs = {
  name: (typeof fields)[number];
  label: string;
  isHidden?: boolean;
  isChecked?: (value: FieldValue) => boolean;
  onChange?: (
    name: (typeof fields)[number],
    value: string | boolean,
  ) => () => void;
  disabled?: boolean;
  info?: ReactNode;
  options?: { label: string; value: string | number }[];
};

type ConfigurationFormProps = {
  user_configurations: UserConfigurations;
  formMethods: UseFormReturn<Fields>;
  details?: OwnerDetails;
  product?: string;
  onSubmit: (data: Partial<Fields>) => void;
};

export default ({
  user_configurations,
  formMethods,
  product,
  onSubmit,
  details,
}: ConfigurationFormProps) => {
  const onSave = () => {
    onSubmit(formMethods.getValues());
  };

  useEffect(() => {
    // we refetch user_configurations after every patch we make
    // set the updated user_configurations as the default values of the form
    const formValues = { ...user_configurations };
    // remove any field that isn't in the list of configuration properties
    Object.keys(formValues).forEach((key) => {
      if (!fields.includes(key as keyof Fields)) {
        delete formValues[key as keyof Fields];
      }
    });

    formMethods.reset(formValues);
  }, [user_configurations, formMethods.reset]);

  const maxMarginMultiplier = formMethods.watch("max_margin_multiplier");
  const canShort = parseFloat(`${maxMarginMultiplier}`) > 1;

  const formInputs: FormInputs[] = [
    { name: "suspend_trade", label: "Trades Suspended" },
    {
      name: "trade_confirm_email",
      label: "Trade Confirmation Email",
      isHidden: product === "paper",
      isChecked: (value) => value === "all" || value === "enabled",
      onChange: (name, oldValue) => () =>
        formMethods.setValue(
          name,
          oldValue === "all" || oldValue === "enabled" ? "none" : "all",
          { shouldDirty: true },
        ),
    },
    { name: "fractional_trading", label: "Fractional Trading" },
    {
      name: "no_shorting",
      label: "Shorting Enabled",
      isChecked: (value) => !value && canShort,
      onChange: (name, oldValue) => () =>
        formMethods.setValue(name, !oldValue, {
          shouldDirty: true,
        }),
      disabled: canShort,
    },
    {
      name: "ptp_no_exception_entry",
      label: "Allow PTP Entry",
      info: (
        <div>
          Please refer to our PTP disclosure &nbsp;
          <Link
            href={PTP_DISCLOSURE_LINK}
            className="pointer-events-auto text-yellow-200"
          >
            here
          </Link>
        </div>
      ),
      isHidden: details?.country_of_tax_residence === "USA",
    },
    {
      name: "max_margin_multiplier",
      label: "Max Margin Multiplier",
      options: MAX_MARGIN_MULTIPLIER_OPTIONS.map((option) => ({
        label: option === 4 ? "4 (Default)" : `${option}`,
        value: option,
      })),
    },
  ];

  const {
    component: MultiplierModal,
    isOpen: isMultiplierModalOpen,
    toggle: multiplierModalToggle,
  } = useModal({
    size: "sm",
    title: "Update Max Margin Multiplier",
    content: (
      <div>
        <Text className="text-rose-700">Warning:</Text>
        <Text>
          You are about to change your max margin multiplier from&nbsp;
          <b>{user_configurations?.max_margin_multiplier}</b> to&nbsp;
          <b>{formMethods.watch("max_margin_multiplier")}</b>. This may affect
          your buying power immediately.
        </Text>
      </div>
    ),
    controls: (
      <div className="flex w-full gap-4">
        <Button
          variant="alternate"
          className="w-full"
          onClick={() => {
            multiplierModalToggle();
          }}
        >
          Cancel
        </Button>
        <Button
          className="w-full"
          onClick={() => {
            formMethods.handleSubmit(onSubmit)();
            multiplierModalToggle();
          }}
        >
          Confirm
        </Button>
      </div>
    ),
    onClose: () => {
      // reset if just closed without clicking either button
      if (isMultiplierModalOpen) {
        formMethods.reset();
      }
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: this effect auto-saves on form dirty state. onSave and multiplierModalToggle are recreated every render, so listing them would submit the form (or reopen the modal) repeatedly. The dirtyFields read is a snapshot taken when isDirty flips.
  useEffect(() => {
    // Auto save every field except for max_margin_multiplier
    if (formMethods.formState.isDirty) {
      if (!formMethods.formState.dirtyFields.max_margin_multiplier) {
        onSave();
      } else {
        multiplierModalToggle();
      }
    }
  }, [formMethods.formState.isDirty]);

  return (
    <>
      <form onSubmit={formMethods.handleSubmit(onSubmit)}>
        {formInputs
          .filter(({ options }) => !options)
          .map(
            ({ name, label, isChecked, isHidden, info, onChange }) =>
              !isHidden && (
                <div
                  key={name}
                  className="flex w-full items-center justify-between border-b-2 p-2"
                >
                  <label htmlFor={name} className="flex">
                    {label}

                    {info && (
                      <Tooltip content={info}>
                        <div>
                          <Icon name="InformationCircle" className="ml-1" />
                        </div>
                      </Tooltip>
                    )}
                  </label>

                  <Controller
                    name={name}
                    control={formMethods.control}
                    render={({ field }) => (
                      <Switch
                        isChecked={
                          isChecked ? isChecked(field.value) : !!field.value
                        }
                        onChange={
                          onChange
                            ? onChange(name, field.value as string | boolean)
                            : () =>
                                formMethods.setValue(field.name, !field.value, {
                                  shouldDirty: true,
                                  shouldValidate: true,
                                })
                        }
                      />
                    )}
                  />
                </div>
              ),
          )}

        <div className="mt-2">
          {formInputs
            .filter(({ options }) => options)
            .map(({ name, label, options }) => (
              <div key={name} className="p-2">
                <label htmlFor={name} className="mb-2">
                  {label}
                </label>
                <Controller
                  name={name}
                  control={formMethods.control}
                  render={({ field: { ref: _, ...field } }) => (
                    <Select options={options ?? []} {...field} />
                  )}
                />
              </div>
            ))}
        </div>
      </form>

      {MultiplierModal}
    </>
  );
};
